import React, { useState, useEffect } from "react";
import { FaMapMarkerAlt, FaClock, FaInfoCircle, FaPhoneAlt, FaEnvelope, FaCheckCircle, FaExclamationTriangle, FaTimes, } from "react-icons/fa";
import api from "../api/http";

export default function Contact() {
  const [values, setValues] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Dialog state for popup
  const [dialog, setDialog] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
    items: [],
  });

  const closeDialog = () => setDialog((d) => ({ ...d, open: false }));
  const openSuccess = () =>
    setDialog({
      open: true,
      type: "success",
      title: "Message sent!",
      message: "Thanks! Your message has been received. We’ll contact you soon.",
      items: [],
    });
  const openError = (msg, items = []) =>
    setDialog({
      open: true,
      type: "error",
      title: "Couldn’t send message",
      message: msg || "Something went wrong. Please try again.",
      items,
    });

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && closeDialog();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const setField = (field) => (e) => {
    setValues((v) => ({ ...v, [field]: e.target.value }));
    setErrors((er) => ({ ...er, [field]: "" }));
  };

  // Validation with phone
  const validate = () => {
    const er = {};
    if (!values.name.trim()) er.name = "Please enter your name.";
    if (!values.email.trim()) {
      er.email = "Please enter your email address.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
      er.email = "Please enter a valid email address.";
    }

    const phone = values.phone.trim();
    if (!phone) {
      er.phone = "Please enter your phone number.";
    } else if (!/^\d{10}$/.test(phone)) {
      er.phone = "Phone number must be exactly 10 digits.";
    }

    if (!values.message.trim()) er.message = "Please include a message.";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      openError("Please fix the highlighted fields and try again.", [
        ...Object.values(errors).filter(Boolean),
      ]);
      return;
    }

    try {
      setSubmitting(true);
      await api.post("/api/contact", {
        name: values.name.trim(),
        email: values.email.trim(),
        phone: values.phone.trim(),
        message: values.message.trim(),
      });

      setValues({ name: "", email: "", phone: "", message: "" });
      setErrors({});
      openSuccess();
    } catch (err) {
      const apiErrors = err?.response?.data?.errors || {};
      const mapped = {};
      if (apiErrors.name) mapped.name = apiErrors.name[0];
      if (apiErrors.email) mapped.email = apiErrors.email[0];
      if (apiErrors.phone) mapped.phone = apiErrors.phone[0];
      if (apiErrors.message) mapped.message = apiErrors.message[0];
      setErrors(mapped);

      if (Object.keys(mapped).length) {
        openError("Please fix the highlighted fields and try again.", [
          ...Object.values(mapped),
        ]);
      } else {
        openError("Sorry, something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="scroll-mt-28 min-h-screen pt-6">
      <div id="contact-sentinel" className="h-1" aria-hidden="true" />

      {/* HERO */}
      <div className="w-full">
        <div className="grid md:grid-cols-3">
          <div
            className="md:col-span-2 relative min-h-[280px] md:min-h-[360px] lg:min_h-[420px] bg-center bg-cover"
            style={{ backgroundImage: "url(/images/Contactus.avif)" }}
          >
            <div className="absolute inset-0 bg-black/40" />
            <div className="relative z-10 max-w-3xl p-6 md:p-10 lg:p-14 text-white">
              <h1 className="text-4xl md:text-5xl font-extrabold mb-4">
                Contact Us
              </h1>
              <p className="text-xl max-w-xl text-neutral-200 mb-6">
                We’d love to discuss your next project. Reach out for quotations,
                site visits, or general inquiries. Our team will respond promptly.
              </p>
              <a
                href="#get-in-touch"
                className="inline-block px-5 py-3 bg-cyan-400 text-black font-semibold rounded hover:bg-teal-300"
              >
                SEND A MESSAGE
              </a>
            </div>
          </div>

          {/* Contact info */}
          <aside className="bg-cyan-300/90 p-6 md:p-8 space-y-6">
            <h3 className="text-2xl font-bold">Contact Info</h3>

            <div>
              <div className="flex items-center gap-2 text-lg font-bold">
                <FaMapMarkerAlt /> Our Offices
              </div>
              <p>Colombo: No.14/3A, Suvisddarama Road, Colombo - 06.</p>
              <p>Jaffna: No.249/1/1, Kasthuriyar Road, Jaffna.</p>
            </div>

            <div>
              <div className="flex items-center gap-2 text-lg font-bold">
                <FaClock /> Open Office Hours
              </div>
              <div>Monday-Saturday: 9:00 AM – 5:00 PM</div>
              <div>Sunday: Closed</div>
            </div>

            <div>
              <div className="flex items-center gap-2 text-lg font-bold">
                <FaInfoCircle /> General Inquiries
              </div>
              <div className="flex items-center gap-2">
                <FaPhoneAlt className="text-sm" /> +94-77 779 7546
              </div>
              <div className="flex items-center gap-2">
                <FaEnvelope className="text-sm" />
                <a
                  href="mailto:peraengineering.pvt@gmail.com"
                  className="underline"
                >
                  peraengineering.pvt@gmail.com
                </a>
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* Maps + Form */}
      <div
        id="get-in-touch"
        className="max-w-7xl mx-auto px-4 md:px-6 mt-8 md:mt-12 lg:mt-16 scroll-mt-28 pb-14 grid gap-8 md:gap-10 md:grid-cols-2"
      >
        {/* Maps */}
        <div className="space-y-8">
          <div>
            <h4 className="text-lg font-bold mb-2">Colombo Branch</h4>
            <div className="rounded-lg overflow-hidden border-2 border-neutral-400 shadow md:h-[280px]">
              <iframe
                title="Colombo"
                className="w-full h-full"
                src={
                  "https://www.google.com/maps?q=" +
                  encodeURIComponent(
                    "No.14/3A, Suvisddarama Road, Colombo 06, Sri Lanka"
                  ) +
                  "&output=embed"
                }
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          <div>
            <h4 className="text-lg font-bold mb-2">Jaffna Branch</h4>
            <div className="rounded-lg overflow-hidden border-2 border-neutral-400 shadow md:h-[280px]">
              <iframe
                title="Jaffna"
                className="w-full h-full"
                src={
                  "https://www.google.com/maps?q=" +
                  encodeURIComponent(
                    "No.249/1/1, Kasthuriyar Road, Jaffna, Sri Lanka"
                  ) +
                  "&output=embed"
                }
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="relative overflow-hidden rounded-2xl bg-white/90 backdrop-blur ring-1 ring-neutral-200 shadow-xl">
          <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-teal-400 via-sky-400 to-teal-400" />
          <div className="p-6 md:p-8">
            <div className="mb-6">
              <h4 className="text-3xl font-extrabold tracking-tight">
                Get In Touch
              </h4>
              <p className="text-sm text-neutral-600 mt-1">
                We usually reply within 24 hours.
              </p>
            </div>

            <form className="space-y-5" onSubmit={onSubmit} noValidate>
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-black mb-1">
                  Name
                </label>
                <input
                  type="text"
                  name="name"
                  placeholder="Your Name"
                  value={values.name}
                  onChange={setField("name")}
                  className={`w-full rounded-lg px-4 py-3 border bg-white/80 placeholder:text-neutral-400 focus:outline-none focus:ring-1 ${
                    errors.name
                      ? "border-red-500 focus:ring-red-500"
                      : "border-neutral-300 focus:ring-teal-500"
                  }`}
                  required
                />
                {errors.name && (
                  <p className="text-red-600 text-sm mt-1">{errors.name}</p>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-black mb-1">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  placeholder="Your Email"
                  value={values.email}
                  onChange={setField("email")}
                  className={`w-full rounded-lg px-4 py-3 border bg-white/80 placeholder:text-neutral-400 focus:outline-none focus:ring-1 ${
                    errors.email
                      ? "border-red-500 focus:ring-red-500"
                      : "border-neutral-300 focus:ring-teal-500"
                  }`}
                  required
                />
                {errors.email && (
                  <p className="text-red-600 text-sm mt-1">{errors.email}</p>
                )}
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-black mb-1">
                  Phone
                </label>
                <input
                  type="tel"
                  name="phone"
                  placeholder="Your Phone Number"
                  value={values.phone}
                  onChange={setField("phone")}
                  onInput={(e) => {
                    const onlyDigits = e.target.value.replace(/[^0-9]/g, "");
                    e.target.value = onlyDigits.slice(0, 10);
                  }}
                  maxLength={10}
                  inputMode="numeric"
                  pattern="\d{10}"
                  className={`w-full rounded-lg px-4 py-3 border bg-white/80 placeholder:text-neutral-400 focus:outline-none focus:ring-1 ${
                    errors.phone
                      ? "border-red-500 focus:ring-red-500"
                      : "border-neutral-300 focus:ring-teal-500"
                  }`}
                  required
                />
                {errors.phone && (
                  <p className="text-red-600 text-sm mt-1">{errors.phone}</p>
                )}
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-black mb-1">
                  Message
                </label>
                <textarea
                  rows="5"
                  name="message"
                  placeholder="Your Message"
                  value={values.message}
                  onChange={setField("message")}
                  className={`w-full rounded-lg px-4 py-3 border bg-white/80 placeholder:text-neutral-400 focus:outline-none focus:ring-1 ${
                    errors.message
                      ? "border-red-500 focus:ring-red-500"
                      : "border-neutral-300 focus:ring-teal-500"
                  }`}
                  required
                />
                {errors.message && (
                  <p className="text-red-600 text-sm mt-1">{errors.message}</p>
                )}
              </div>

              {/* Submit */}
              <div className="flex items-center">
                <button
                  type="submit"
                  disabled={submitting}
                  aria-disabled={submitting}
                  className="ml-auto flex items-center gap-2 px-6 py-3 bg-cyan-500 text-white font-semibold rounded hover:bg-cyan-400 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? "Sending..." : "Send Message"}
                  {!submitting && (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-5 h-5"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M2.01 21 23 12 2.01 3 2 10l15 2-15 2z" />
                    </svg>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Popup Dialog */}
      {dialog.open && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={closeDialog} />
          <div className="relative z-10 w-full max-w-md mx-auto bg-white rounded-2xl shadow-2xl p-6 ring-1 ring-neutral-200">
            <button
              aria-label="Close"
              onClick={closeDialog}
              className="absolute top-3 right-3 text-neutral-400 hover:text-neutral-600"
            >
              <FaTimes />
            </button>

            <div className="flex items-start gap-3">
              <div
                className={`mt-1 text-3xl ${
                  dialog.type === "success" ? "text-green-500" : "text-red-500"
                }`}
              >
                {dialog.type === "success" ? (
                  <FaCheckCircle />
                ) : (
                  <FaExclamationTriangle />
                )}
              </div>

              <div>
                <h3 className="text-xl font-bold">{dialog.title}</h3>
                <p className="text-sm text-neutral-600 mt-1">
                  {dialog.message}
                </p>

                {dialog.items?.length > 0 && (
                  <ul className="mt-3 list-disc list-inside text-sm text-neutral-700 space-y-1">
                    {dialog.items.map((it, i) => (
                      <li key={i}>{it}</li>
                    ))}
                  </ul>
                )}

                <div className="mt-5 flex justify-end">
                  <button
                    onClick={closeDialog}
                    className={`px-4 py-2 rounded-lg ${
                      dialog.type === "success"
                        ? "bg-cyan-500 text-white hover:bg-cyan-400"
                        : "ring-1 ring-neutral-300 hover:bg-neutral-50"
                    }`}
                  >
                    {dialog.type === "success" ? "Done" : "OK"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
